import { FileInfo, FileListQuery, FileListResponse } from '../types/certificate';
import appConfig from '../config/app.config';

/**
 * 文件管理服务
 * 提供文件上传、下载、删除、查询等功能
 */

const API_BASE_URL = appConfig.API_BASE_URL;

/**
 * API请求工具函数
 */
const apiRequest = async (url: string, options: RequestInit = {}) => {
  const response = await fetch(`${API_BASE_URL}${url}`, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch (e) {
      errorData = { message: `HTTP错误: ${response.status} ${response.statusText}` };
    }
    
    console.error('API请求失败:', {
      url: `${API_BASE_URL}${url}`,
      status: response.status,
      statusText: response.statusText,
      errorData
    });
    
    throw new Error(errorData.message || `HTTP错误: ${response.status}`);
  }

  return response.json();
};

/**
 * 格式化文件大小
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * 格式化日期
 */
export const formatDate = (dateStr: string): string => {
  if (!dateStr) return '-';
  
  try {
    const date = new Date(dateStr);
    
    // 检查日期是否有效
    if (isNaN(date.getTime())) {
      return dateStr;
    }
    
    // 如果数据库时间是UTC，需要手动加8小时转为中国时间
    const chinaTime = new Date(date.getTime() + 8 * 60 * 60 * 1000);
    
    // 使用本地时区格式化
    return chinaTime.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('日期格式化错误:', error);
    return dateStr;
  }
};

/**
 * 获取文件列表
 */
export const getFileList = async (query: FileListQuery = {}): Promise<FileListResponse> => {
  try {
    const searchParams = new URLSearchParams();
    
    // 构建查询参数
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });

    const response = await apiRequest(`/files?${searchParams.toString()}`);
    return response;
  } catch (error) {
    console.error('获取文件列表失败:', error);
    throw error;
  }
};

/**
 * 删除文件
 */
export const deleteFile = async (fileId: string): Promise<void> => {
  try {
    await apiRequest(`/files/${fileId}`, {
      method: 'DELETE',
    });
  } catch (error) {
    console.error('删除文件失败:', error);
    throw error;
  }
};

/**
 * 批量删除文件
 */
export const deleteFiles = async (fileIds: string[]): Promise<void> => {
  try {
    // 串行删除每个文件
    for (const fileId of fileIds) {
      await deleteFile(fileId);
    }
  } catch (error) {
    console.error('批量删除文件失败:', error);
    throw error;
  }
};

/**
 * 获取存储统计信息
 */
export const getStorageStats = async () => {
  try {
    const response = await apiRequest('/files/stats');
    return response;
  } catch (error) {
    console.error('获取存储统计失败:', error);
    throw error;
  }
};

/**
 * 更新文件下载次数
 */
export const incrementDownloadCount = async (fileId: string): Promise<void> => {
  try {
    await apiRequest(`/files/${fileId}/download`, {
      method: 'POST',
    });
  } catch (error) {
    console.error('更新下载计数失败:', error);
    // 不抛出错误，因为这不是关键功能
  }
};

/**
 * 下载文件（增强版）
 */
export const downloadFileEnhanced = async (file: {
  id: string;
  filename: string;
  url: string;
  orderNumber: string;
}): Promise<void> => {
  try {
    // 更新下载计数
    await incrementDownloadCount(file.id);
    
    // 获取文件内容
    const response = await fetch(file.url, {
      method: 'GET',
      headers: {
        'Accept': 'application/pdf,*/*',
      },
    });

    if (!response.ok) {
      throw new Error(`下载失败: ${response.status}`);
    }

    // 获取文件内容
    const blob = await response.blob();
    
    // 创建下载链接
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = file.filename || `${file.orderNumber}.pdf`;
    
    // 触发下载
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // 清理URL对象
    window.URL.revokeObjectURL(downloadUrl);
    
  } catch (error) {
    console.error('下载文件失败:', error);
    throw error;
  }
};

/**
 * 预览文件（增强版）
 */
export const previewFileEnhanced = async (file: {
  id: string;
  filename: string;
  url: string;
  type: string;
}): Promise<void> => {
  try {
    // 检查文件是否可访问
    const response = await fetch(file.url, {
      method: 'HEAD',
      headers: {
        'Accept': 'application/pdf,*/*',
      },
    });

    if (!response.ok) {
      throw new Error(`文件不可访问: ${response.status}`);
    }

    // 对于PDF文件，在新窗口中打开
    if (file.type === 'certificate' || file.filename.toLowerCase().endsWith('.pdf')) {
      window.open(file.url, '_blank', 'noopener,noreferrer');
    } else {
      // 对于其他文件类型，直接在新窗口打开
      window.open(file.url, '_blank');
    }
    
  } catch (error) {
    console.error('预览文件失败:', error);
    throw error;
  }
};

/**
 * 获取文件完整URL
 */
export const getFileFullUrl = (relativePath: string): string => {
  // 如果是相对路径，添加API基础URL
  if (relativePath.startsWith('/uploads/')) {
    return `${API_BASE_URL.replace('/api', '')}${relativePath}`;
  }
  return relativePath;
};

/**
 * 添加测试文件（开发阶段使用）
 */
export const addTestFile = async (testData: {
  orderNumber: string;
  type: 'certificate' | 'report';
  filename?: string;
}) => {
  try {
    const response = await apiRequest('/files/test', {
      method: 'POST',
      body: JSON.stringify(testData),
    });
    return response;
  } catch (error) {
    console.error('添加测试文件失败:', error);
    throw error;
  }
};

/**
 * 创建合格证
 */
export const createCertificate = async (certificateData: {
  orderNumber: string;
  batchNumber: string;
  bucketCount: number;
  productionDate: string;
  shipmentDate: string;
  testReportDate: string;
}) => {
  try {
    const response = await apiRequest('/certificates', {
      method: 'POST',
      body: JSON.stringify(certificateData),
    });
    return response;
  } catch (error) {
    console.error('创建合格证失败:', error);
    throw error;
  }
};

/**
 * 保存合格证PDF文件
 */
export const saveCertificatePDF = async (certificateData: {
  orderNumber: string;
  batchNumber: string;
  bucketCount: number;
  productionDate: string;
  shipmentDate: string;
  testReportDate: string;
  pdfBase64: string;
  reportId?: string;
}) => {
  try {
    const response = await apiRequest('/files/certificate-pdf', {
      method: 'POST',
      body: JSON.stringify(certificateData),
    });
    return response;
  } catch (error) {
    console.error('保存合格证PDF失败:', error);
    throw error;
  }
};

/**
 * 保存检测报告PDF文件
 */
export const saveTestReportPDF = async (reportData: {
  orderNumber: string;
  quantity: number;
  batchNumber: string;
  productionDate: string;
  generationDate: string;
  pdfBase64: string;
  certificateId?: string;
}) => {
  try {
    const response = await apiRequest('/files/test-report-pdf', {
      method: 'POST',
      body: JSON.stringify(reportData),
    });
    return response;
  } catch (error) {
    console.error('保存检测报告PDF失败:', error);
    throw error;
  }
};

/**
 * 保存合格证和检测报告（关联保存）
 */
export const saveCertificateWithReport = async (
  certificateData: any,
  reportData: any,
  reportId?: string
): Promise<any> => {
  try {
    console.log('开始保存合格证和检测报告...');
    console.log('API请求地址:', `${API_BASE_URL}/files/certificate-with-report`);
    
    // 如果提供了reportId，将其传递给后端用于文件关联
    const requestData = {
      certificateData: {
        ...certificateData,
        reportId: reportId // 确保reportId被传递
      },
      reportData: {
        ...reportData,
        reportId: reportId
      }
    };
    
    console.log('请求数据结构:', {
      certificateData: {
        orderNumber: requestData.certificateData.orderNumber,
        reportId: requestData.certificateData.reportId,
        pdfBase64Length: requestData.certificateData.pdfBase64?.length || 0,
        hasPdfData: !!requestData.certificateData.pdfBase64
      },
      reportData: {
        orderNumber: requestData.reportData.orderNumber,
        reportId: requestData.reportData.reportId,
        pdfBase64Length: requestData.reportData.pdfBase64?.length || 0,
        hasPdfData: !!requestData.reportData.pdfBase64
      }
    });
    
    const result = await apiRequest('/files/certificate-with-report', {
      method: 'POST',
      body: JSON.stringify(requestData),
    });
    
    console.log('保存成功:', result);
    return result;
  } catch (error) {
    console.error('保存合格证和检测报告失败:', error);
    throw error;
  }
};

/**
 * 获取合格证列表
 */
export const getCertificateList = async (query: {
  page?: number;
  pageSize?: number;
  orderNumber?: string;
} = {}) => {
  try {
    const searchParams = new URLSearchParams();
    
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });

    const response = await apiRequest(`/certificates?${searchParams.toString()}`);
    return response;
  } catch (error) {
    console.error('获取合格证列表失败:', error);
    throw error;
  }
};